import React, { useState } from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const IE_CONTACT = {
  name: 'eCOMM Merchant Solutions Ireland Limited',
  address: '2 Bedford Place, Navan, Co. Meath, C15 VRF3, Ireland',
  phone: '+353 (0)46 901 1288',
  email: 'csr@ecomm365.com',
};
const UK_CONTACT = {
  name: 'eCOMM Merchant Solutions Limited',
  address: '86-90 Paul Street, London, EC2A 4NF, United Kingdom',
  phone: '+44 (0)20 7183 5253',
  email: 'csr@ecomm365.com',
};

const DPO = {
  name: 'Abraham Nwadiani',
  email: 'DPO@ecomm365.com',
};

const SUPERVISORY_IE = {
  name: 'Data Protection Commission',
  address: '21 Fitzwilliam Square South, Dublin 2, D02 RD28, Ireland',
  phone: '+353 (0)1 765 0100',
  website: 'https://www.dataprotection.ie/',
};
const SUPERVISORY_UK = {
  name: 'The Information Commissioners Office',
  address: 'Wycliff House, Water Lane, Wilmslow, Cheshire, SK9 5AF, United Kingdom',
  phone: '+44 (0)303 123 1113',
  email: 'casework@ico.org.uk',
  website: 'https://www.ico.org.uk/',
};

const RegionToggle = ({ region, onChange }) => (
  <div
    className="inline-flex bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20"
    data-testid="privacy-region-toggle"
  >
    <button
      type="button"
      onClick={() => onChange('ie')}
      className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
        region === 'ie' ? 'bg-white text-[#0a1929]' : 'text-white/85 hover:text-white'
      }`}
      data-testid="privacy-region-ie"
    >
      Ireland (EU)
    </button>
    <button
      type="button"
      onClick={() => onChange('uk')}
      className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
        region === 'uk' ? 'bg-white text-[#0a1929]' : 'text-white/85 hover:text-white'
      }`}
      data-testid="privacy-region-uk"
    >
      United Kingdom
    </button>
  </div>
);

const Definitions = () => (
  <>
    <h3>Definitions</h3>
    <p>
      The data Privacy Notice is based on the terms used by the European legislator for the
      adoption of the General Data Protection Regulation (GDPR). For ease of understanding the
      following definitions apply:
    </p>
    <p><strong>Controller:</strong> the natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the processing of personal data.</p>
    <p><strong>Personal data:</strong> any information relating to an identified or identifiable natural person ('Data Subject').</p>
    <p><strong>Data Subject:</strong> any identified or identifiable natural person, whose personal data is processed by the controller.</p>
    <p><strong>Processor:</strong> a natural or legal person, public authority, agency or other body which processes personal data on behalf of the controller.</p>
    <p><strong>Recipient:</strong> a natural or legal person, public authority, agency or another body, to which the personal data are disclosed.</p>
    <p><strong>Third Party:</strong> a natural or legal person, public authority, agency or body other than the data subject, controller, processor and persons authorised to process personal data.</p>
    <p><strong>Restriction of processing:</strong> the marking of stored personal data with the aim of limiting their processing in the future.</p>
    <p><strong>Processing:</strong> any operation performed on personal data, such as collection, recording, storage, retrieval, use, disclosure, erasure or destruction.</p>
    <p><strong>Profiling:</strong> any form of automated processing of personal data to evaluate certain personal aspects of a natural person.</p>
    <p><strong>Consent:</strong> any freely given, specific, informed and unambiguous indication of the Data Subject's wishes signifying agreement to the processing of personal data.</p>
  </>
);

const ContactCard = ({ title, contact }) => (
  <div className="info-card">
    <h4>{title}</h4>
    <p><strong>{contact.name}</strong></p>
    <p>{contact.address}</p>
    {contact.phone && <p>Phone: <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a></p>}
    {contact.email && <p>Email: <a href={`mailto:${contact.email}`}>{contact.email}</a></p>}
    {contact.website && <p>Website: <a href={contact.website} target="_blank" rel="noreferrer">{contact.website.replace(/^https?:\/\//, '')}</a></p>}
  </div>
);

const Body = ({ region }) => {
  const controller = region === 'ie' ? IE_CONTACT : UK_CONTACT;
  const supervisory = region === 'ie' ? SUPERVISORY_IE : SUPERVISORY_UK;
  const shareEntity = region === 'ie' ? 'Revenue Commissioners' : 'HMRC';
  const company = region === 'ie' ? 'eCOMM Merchant Solutions Ireland Limited (EMSIL)' : 'eCOMM Merchant Solutions Limited (EMS)';

  return (
    <>
      <h2>Introduction</h2>
      <p>
        {company} takes data protection very seriously. The use of our Internet pages is not
        possible without the provision of some personal data; however, if a Data Subject wishes
        to use certain services via our website, processing of further personal data could become
        necessary. If the processing of personal data is necessary and there is no statutory
        basis for such processing, we will obtain consent from the data subject.
      </p>
      <p>
        Personal Data processing shall always be in line with the General Data Protection
        Regulation (GDPR) and applicable data protection legislation. By means of this Privacy
        Notice we inform the general public why we collect and process personal data and outline
        Data Subject rights relating to the collection and processing of their Personal Data.
      </p>

      <Definitions />

      <ContactCard title="Name and Address of the Controller" contact={controller} />
      <ContactCard title="Data Protection Officer" contact={{ ...IE_CONTACT, name: DPO.name, email: DPO.email }} />
      <ContactCard title="Lead Supervisory Authority" contact={supervisory} />

      <h2>Purpose for Processing Information</h2>
      <p>
        The following is a broad description of the way we process personal information. To
        understand how your own personal information is processed you may also need to refer to
        any personal communications you have received.
      </p>
      <p>
        We process personal information to enable us to promote our services, provide Merchants
        with e-money services, administer Merchant Accounts, manage queries, process
        transactions, and manage complaints. We also process personal information to meet our
        legal and regulatory obligations including the prevention and detection of crime such as
        money laundering, the reporting of suspicious transactions, requirements to check our
        records against financial sanctions lists and meeting our legal obligations such as the
        Consumer Protection Code.
      </p>
      <p>
        From time to time we may request feedback from our customers in order to improve the
        service we offer, for example by conducting customer satisfaction surveys.
      </p>
      <p>
        We may also use your personal data when we are testing enhancements to our IT systems or
        websites to help ensure that the changes we make are tested in as real an environment as
        possible, thereby minimising the impact on our customers.
      </p>
      <p>
        For customer service and quality control purposes, we may record and monitor calls in
        order to assess the quality of our staff's customer service, to verify your identity, to
        administer your policy and to improve our services to you.
      </p>

      <h3 data-toc-skip="true">We collect information from the following sources:</h3>
      <ul>
        <li>The data subject directly (e.g. when you complete an online application form)</li>
        <li>The data subject indirectly (e.g. when you complete an application form with an Introducer or reseller)</li>
        <li>Publicly available registers (e.g. the electoral roll)</li>
        <li>Social Media</li>
        <li>Credit searches internally or with one or more credit checking or credit reference agencies</li>
        <li>Relevant law enforcement agencies</li>
        <li>Screening agencies</li>
        <li>Government Agencies and Bodies</li>
      </ul>

      <h3 data-toc-skip="true">This information may include:</h3>
      <ul>
        <li>Personal details such as full name, date of birth, nationality, email address, home address and phone number</li>
        <li>Business activities of the person whose personal information we are processing</li>
        <li>Goods and services provided</li>
        <li>Financial details such as bank account details</li>
        <li>Professional Body membership</li>
      </ul>

      <h3 data-toc-skip="true">We also process sensitive classes of information that may include:</h3>
      <ul>
        <li>Offences and alleged offences</li>
      </ul>

      <h3 data-toc-skip="true">We process personal information about our:</h3>
      <ul>
        <li>Customers (merchants)</li>
        <li>Complainants and enquirers</li>
        <li>Suppliers</li>
        <li>Advisers and other professional experts</li>
        <li>Employees</li>
      </ul>

      <h3 data-toc-skip="true">Where necessary or required we share information with:</h3>
      <ul>
        <li>{shareEntity}</li>
        <li>Regulators</li>
        <li>Card Schemes</li>
        <li>Service Providers</li>
        <li>Professional Advisors</li>
        <li>Courts and those involved in legal proceedings</li>
        <li>Anyone in the future who may buy or merge with our business</li>
      </ul>

      <h2>Online Services</h2>
      <p>
        When you use our online services, we gather certain information automatically and store
        it in files. This information includes IP addresses, browser type, internet service
        provider, referring/exit pages, date timestamp and click-stream path. This data does not
        contain any personal identifiable information and is used to analyse trends, administer
        the site and track users' movements around the website during a particular session and
        to gather demographic data.
      </p>

      <h2>Cookies</h2>
      <p>
        Cookies are small text files stored by your browser. They help us to track information on
        our systems and identify categories of visitors by using information such as your IP
        address, domain, browser type and pages visited. For further information please see our{' '}
        <a href="/cookies-policy">Cookie Policy</a>.
      </p>

      <h2>Rights of the Data Subject</h2>
      <p>GDPR affords Data Subjects with rights, summarised below. To assert any of these rights, you may contact the Data Protection Officer at any time.</p>
      <p><strong>Right of Confirmation:</strong> the right to obtain confirmation as to whether or not personal data concerning you are being processed.</p>
      <p><strong>Right of Access:</strong> the right to obtain information and a copy of personal data stored about you.</p>
      <p><strong>Right of Rectification:</strong> the right to obtain rectification of inaccurate personal data without undue delay.</p>
      <p><strong>Right of Erasure (right to be forgotten):</strong> the right to obtain erasure of personal data where statutory grounds apply.</p>
      <p><strong>Right of Restriction of Processing:</strong> the right to obtain restriction of processing where statutory reasons apply.</p>
      <p><strong>Right to Object:</strong> the right to object to processing on grounds relating to your particular situation.</p>
      <p><strong>Automated individual decision-making, including profiling:</strong> the right not to be subject to a decision based solely on automated processing.</p>
      <p><strong>Right to Withdraw Consent:</strong> where consent forms the basis for processing, the right to withdraw consent at any time.</p>
      <p><strong>Right to Complain to the Supervisory Authority:</strong> details of the Supervisory Authority are above.</p>

      <h2>Legal Basis for the Processing</h2>
      <p>
        We will only collect, use and share your information where we have a valid reason to do
        so under data protection legislation. We have three main reasons set out below:
      </p>
      <ol>
        <li>
          <strong>Contractual obligations</strong> — we process personal details as part of the
          application process. Once the contract is in place, processing is necessary for the
          performance of this contract including: using bank account details to process payments;
          verifying identity; underwriting and administering your Merchant Account; managing
          complaints; and maintaining records on our systems. If you do not provide us with your
          personal data for these purposes, we may not be able to provide services to you.
        </li>
        <li>
          <strong>Legal obligations</strong> — we may need certain information from you in order
          to meet our legal obligations (for example, proof of identity to meet anti-money-laundering obligations) and for reporting to supervisory authorities.
        </li>
        <li>
          <strong>Our legitimate business interests</strong> — we strive to continually improve
          how we do business with you. We may also process personal data for preventing fraud,
          internal administration, and reporting potential criminal acts to a competent authority.
          Before using your personal data to pursue our legitimate interests, the impact of our
          processing activities is carefully considered against the fundamental rights and
          freedoms of individuals.
        </li>
      </ol>

      <h2>Security of Processing</h2>
      <p>
        As the Controller, {company} has implemented technical and organisational measures to
        ensure personal data processed remains secure; however absolute security cannot be
        guaranteed. Should you have a particular concern about a method of data transmission, we
        will take reasonable steps to provide an alternative.
      </p>

      <h2>Transfers</h2>
      <p>
        It may sometimes be necessary to transfer personal information overseas. Any transfers
        made will be in full compliance with the General Data Protection Regulation and
        country-specific legislation applicable to {company}.
      </p>

      <h2>Personal Data Retention Periods</h2>
      <p>
        The criteria used to determine the retention period of personal data is the respective
        statutory retention period within the State. After the expiration of that period,
        personal data shall be securely deleted unless still necessary for the fulfilment of the
        contract, the initiation of a contract, or in relation to other legal proceedings.
      </p>

      <h2>Automated Decision Making & Profiling</h2>
      <p>{company} does not process personal data for automatic decision-making or profiling.</p>

      <h2>Data Protection for Employment & Recruitment</h2>
      <p>
        The data controller shall collect and process personal data of applicants for the
        processing of the application procedure. If no employment contract is concluded, the
        application documents shall be automatically erased two months after notification of the
        refusal decision, provided that no other legitimate interests are opposed to the erasure.
      </p>

      <h2>General</h2>
      <p>
        You may not transfer any of your rights under this privacy notice to any other person. We
        may transfer our rights under this privacy notice where we reasonably believe your rights
        will not be affected. This notice will be governed by and interpreted according to{' '}
        {region === 'ie' ? 'the law of Ireland' : 'the laws of England and Wales'}. All disputes
        arising under the notice will be subject to the exclusive jurisdiction of{' '}
        {region === 'ie' ? 'the Irish courts' : 'the English and Welsh courts'}.
      </p>

      <h2>Changes to this Notice</h2>
      <p>
        We may change this policy to reflect changes in the law or our privacy practices. However
        we will not use your personal data in any new ways without your consent.
      </p>
    </>
  );
};

const PrivacyPolicy = () => {
  const [region, setRegion] = useState('ie');

  return (
    <LegalPageLayout
      eyebrow="Legal · Data Protection"
      title={region === 'ie' ? 'Privacy Policy' : 'Privacy Policy (UK)'}
      lead={
        region === 'ie'
          ? 'eCOMM Merchant Solutions Ireland Limited — how we collect, process and protect your personal data under the GDPR and Irish data protection legislation.'
          : 'eCOMM Merchant Solutions Limited — how we collect, process and protect your personal data under UK GDPR and the Data Protection Act 2018.'
      }
      lastUpdated={region === 'ie' ? 'June 2026' : 'June 2026'}
      topRightSlot={<RegionToggle region={region} onChange={setRegion} />}
      tocRefreshKey={region}
      testId="privacy-policy-page"
    >
      <Body region={region} />
    </LegalPageLayout>
  );
};

export default PrivacyPolicy;
